import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'

function SignUpForm({ onSuccess }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [privacyAck, setPrivacyAck] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const emailLower = email.trim().toLowerCase()

      const { data, error } = await supabase.auth.signUp({
        email: emailLower,
        password,
      })

      if (error) throw error

      await supabase.from('audit_logs').insert({
        event_type: 'user_signup',
        metadata: {
          email: emailLower,
          privacy_acknowledged: privacyAck,
        },
      })

      onSuccess?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    if (!privacyAck) {
      setError('Please acknowledge the privacy policy before continuing')
      return
    }

    setError(null)
    setLoading(true)

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/team-setup`
        }
      })

      if (error) throw error
      // OAuth will redirect, so we don't call onSuccess here
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="email"
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        disabled={loading}
      />
      
      <Input
        type="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        disabled={loading}
        minLength={6}
      />

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-start gap-3 p-3 bg-brand-cream border border-border rounded">
        <input
          type="checkbox"
          id="privacy-ack"
          className="mt-1"
          checked={privacyAck}
          onChange={(e) => setPrivacyAck(e.target.checked)}
        />
        <label htmlFor="privacy-ack" className="text-sm text-brand-charcoal">
          <strong>I agree to Bench Bulletin's youth data privacy policy</strong> and confirm I will only email parents who have given consent.
        </label>
      </div>

      <Button
        type="submit"
        variant="primary"
        disabled={loading || !privacyAck}
        className="w-full"
      >
        {loading ? 'Creating account...' : 'Sign Up'}
      </Button>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or continue with</span>
        </div>
      </div>

      {/* Google Sign Up */}
      <Button
        type="button"
        variant="secondary"
        disabled={loading || !privacyAck}
        onClick={handleGoogleSignUp}
        className="w-full flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Sign up with Google
      </Button>
    </form>
  )
}

export default SignUpForm

